import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useSwipeGesture = (isSwipeDisabled: boolean = false) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const tabRoutes = [
      '/dashboard/feed',
      '/dashboard/facilities',
      '/dashboard/activities',
      '/dashboard/messages'
    ];

    const normalize = (path: string) => path.replace(/\/+$/, '').toLowerCase();

    const animateAndNavigate = (targetRoute: string) => {
      const root = document.getElementById('root');
      if (root) {
        root.classList.add('fade-out');
        setTimeout(() => {
          navigate(targetRoute);
          root.classList.remove('fade-out');
        }, 200);
      } else {
        navigate(targetRoute);
      }
    };

    (window as any).navigateToNextTab = () => {
      const current = normalize(location.pathname);
      const i = tabRoutes.findIndex(route => normalize(route) === current);
      if (i !== -1 && i < tabRoutes.length - 1) {
        animateAndNavigate(tabRoutes[i + 1]);
      }
    };

    (window as any).navigateToPreviousTab = () => {
      const current = normalize(location.pathname);
      const i = tabRoutes.findIndex(route => normalize(route) === current);
      if (i > 0) {
        animateAndNavigate(tabRoutes[i - 1]);
      }
    };

    let startX = 0, startY = 0, startTarget: EventTarget | null = null;

    const isInsideNoSwipeZone = (target: EventTarget | null): boolean => {
      let node = target as HTMLElement | null;
      while (node) {
        if (
          node.id === 'no-swipe-zone' ||
          node.classList?.contains('no-swipe') ||
          node.hasAttribute('data-no-swipe') ||
          node.closest('[data-no-swipe]') !== null
        ) {
          return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    const isSwipeSensitive = (el: HTMLElement): boolean => {
      const sensitiveTags = ['img', 'canvas'];
      const sensitiveClasses = ['map', 'leaflet', 'gallery', 'slider', 'swiper', 'carousel'];
      const tag = el.tagName.toLowerCase();
      const classList = Array.from(el.classList).map(c => c.toLowerCase());
      if (sensitiveTags.includes(tag)) return true;
      if (classList.some(cls => sensitiveClasses.some(s => cls.includes(s)))) return true;
      return false;
    };

    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTarget = e.target;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (isSwipeDisabled) return;

      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const threshold = 50;
      const maxYThreshold = 30;
      const isModalOpen = (window as any).isModalOpen === true;

      if (isModalOpen || isInsideNoSwipeZone(startTarget)) return;

      if (startTarget instanceof Element) {
        let node: HTMLElement | null = startTarget as HTMLElement;
        while (node) {
          if (
            node.getAttribute('data-prevent-swipe') !== null ||
            node.closest('[data-prevent-swipe]') !== null ||
            node.hasAttribute('data-no-swipe') ||
            node.closest('[data-no-swipe]') !== null ||
            isSwipeSensitive(node)
          ) {
            return;
          }
          node = node.parentElement;
        }
      }

      if (absX > threshold && absY < maxYThreshold) {
        const pathname = normalize(location.pathname);
        const isTabPage = tabRoutes.some(route => normalize(route) === pathname);

        if (!isTabPage) return;

        if (deltaX > 0) {
          window.webkit?.messageHandlers?.swipeEvent?.postMessage?.('swipe-right');
        } else {
          window.webkit?.messageHandlers?.swipeEvent?.postMessage?.('swipe-left');
        }
      }
    };

    document.addEventListener('touchstart', onTouchStart);
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [location.pathname, navigate, isSwipeDisabled]);
};
